import { Space, Tag } from 'antd';
import { ICellRendererProps } from 'common/components/table/table.types';
import { DateToFormat, formatCustomDateTime, StrToDate, StrToDateTimeLocal } from 'common/helpers/date.helper';
import { DATE_SHORT_MMM_DD_YYYY } from 'common/constans/date.constants';
import React from 'react';
import { guestSourceTypeMap } from 'common/constans/guest-source-options.constants';
import { IGuestRead } from '../services/residents-service/residents.service.types';
import BaseCellText from 'common/components/table/base-cell-text';
import { GuestType } from '../enums/guest-type.enum';
import { formatPhone } from 'common/helpers/phone.helper';
import { ReactComponent as KeyIcon } from 'assets/icons/collect-key.svg';
import { mapToGuestValidity } from 'common/helpers/guest-validity.helper';
import { ServerGuestValidity } from 'common/enums/guest-validity.enum';
import { GuestTypeSource } from 'common/enums/guest-type-source.enum';
import { getGuestTypeValue } from 'common/helpers/guest.helper';
import dayjs from 'dayjs';
import { UploadFileTypes } from 'common/services/file-service/file.service.types';
import EIQPreviewImage from 'common/components/image/eiq-preview-image';
import { validityMap } from 'common/constans/common.constants';
import { SortOrder } from 'react-base-table';

const getValidity = (rowData: IGuestRead) => {
  const isPeriod = rowData?.endDate && rowData?.startDate && rowData.validity === ServerGuestValidity.DateRange;

  if (isPeriod) {
    return `${DateToFormat(rowData?.startDate, DATE_SHORT_MMM_DD_YYYY)} - ${DateToFormat(rowData?.endDate, DATE_SHORT_MMM_DD_YYYY)}`;
  }

  var validity = mapToGuestValidity(rowData.validity, StrToDate(rowData?.visitDate));
  return validityMap[validity];
};

export const guestColumns = () => ({
  lastName: {
    key: 'lastName',
    title: 'Last name',
    dataKey: 'lastName',
    width: 195,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
  firstName: {
    key: 'firstName',
    title: 'First name',
    dataKey: 'firstName',
    width: 195,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
  firstNameWithKey: {
    key: 'firstName',
    title: 'First name',
    dataKey: 'firstName',
    width: 195,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      return (
        <BaseCellText>
          {cell.rowData.issuedKeys.length ? (
            <Space className="icon-key-name-cell d-flex" size={4}>
              <KeyIcon /> {cell.rowData.firstName}
            </Space>
          ) : (
            cell.rowData.firstName
          )}
        </BaseCellText>
      );
    },
  },
  phone: {
    key: 'phone',
    title: 'Phone',
    dataKey: 'phone',
    width: 195,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      return formatPhone(cell?.rowData.countryCode, cell?.cellData);
    },
  },
  companyName: {
    key: 'companyName',
    title: 'Company name',
    dataKey: 'companyName',
    width: 190,
    resizable: true,
    sortable: true,
    fixed: 1,
  },
  type: {
    key: 'type',
    title: 'Type',
    dataKey: 'type',
    width: 152,
    resizable: true,
    sortable: true,
    fixed: 1,
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      return (
        <Tag className="eiq-tag" color={guestTypeColorMap[cell?.cellData as GuestType]} style={{ color: '#292B2E' }}>
          {getGuestTypeValue(cell?.cellData)}
        </Tag>
      );
    },
  },
  validity: {
    key: 'validity',
    title: 'Validity',
    dataKey: 'validity',
    width: 195,
    sortable: true,
    resizable: true,
    fixed: 1,
    //For sort
    comparer: (a: IGuestRead, b: IGuestRead, order: SortOrder) => {
      const getGuestSortDate = (guest: IGuestRead): string | null => {
        switch (guest.validity) {
          case ServerGuestValidity.DateRange:
            return guest.startDate;

          case ServerGuestValidity.OneTime:
          case ServerGuestValidity.OneDay:
            return guest.visitDate;

          default:
            return null;
        }
      };

      const permanentA = a.validity === ServerGuestValidity.Permanent;
      const permanentB = b.validity === ServerGuestValidity.Permanent;

      if (permanentA || permanentB) {
        return Number(permanentA) - Number(permanentB);
      }

      const dateA = getGuestSortDate(a);
      const dateB = getGuestSortDate(b);

      const parsedA = dateA ? dayjs.utc(dateA) : null;
      const parsedB = dateB ? dayjs.utc(dateB) : null;

      const validA = parsedA?.isValid() ?? false;
      const validB = parsedB?.isValid() ?? false;

      if (!validA || !validB) {
        return Number(validB) - Number(validA);
      }

      const difference = parsedA!.valueOf() - parsedB!.valueOf();

      return order === 'asc' ? difference : -difference;
    },
    getValue: getValidity,
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      return <BaseCellText>{getValidity(cell?.rowData)}</BaseCellText>;
    },
  },
  daysOfWeek: {
    key: 'daysOfWeek',
    title: 'Days of week for visits',
    dataKey: 'daysOfWeek',
    width: 250,
    sortable: true,
    resizable: true,
    fixed: 1,
    getValue: (rowData: IGuestRead) => rowData.daysOfWeek?.join(', '),
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      const rowData = cell?.rowData;

      return <BaseCellText>{rowData?.daysOfWeek?.length >= 7 ? `All days` : rowData?.daysOfWeek?.join(', ')}</BaseCellText>;
    },
  },
  keyPermission: {
    key: 'isKeyPermissionAllowed',
    title: 'Key permission',
    dataKey: 'isKeyPermissionAllowed',
    width: 195,
    sortable: true,
    resizable: true,
    fixed: 1,
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      return (
        <Tag className="eiq-tag" color={keyPermissionMap[cell?.cellData + '']}>
          {cell?.cellData ? 'Allowed' : 'Not allowed'}
        </Tag>
      );
    },
  },
  lastActivityDate: {
    key: 'lastActivityDate',
    title: 'Last Activity Date',
    dataKey: 'lastActivityDate',
    width: 250,
    sortable: true,
    resizable: true,
    fixed: 1,
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      const local = cell.cellData ? dayjs.utc(cell.cellData).local() : null;
      return formatCustomDateTime(local ? local.toDate() : null, true);
    },
  },
  dateGuestAdded: {
    key: 'createdAt',
    title: 'Date added',
    dataKey: 'createdAt',
    width: 210,
    sortable: true,
    resizable: true,
    fixed: 1,
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      const local = cell.cellData ? dayjs.utc(cell.cellData).local() : null;
      return formatCustomDateTime(local ? local.toDate() : null, true);
    },
  },
  source: {
    key: 'source',
    title: 'Source of guest',
    dataKey: 'source',
    width: 260,
    sortable: true,
    resizable: true,
    fixed: 1,
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      if (cell?.rowData?.source === GuestTypeSource.Resident || cell?.rowData?.source === GuestTypeSource.EIQVoice) {
        return `${guestSourceTypeMap[cell?.rowData?.source]} (${cell.rowData.residentName})`;
      }

      if (cell?.rowData?.source === GuestTypeSource.SecurityOfficer && !!cell?.rowData?.sourceUser) {
        return `${guestSourceTypeMap[cell?.rowData?.source]} (${cell.rowData.sourceUser.accountName})`;
      }

      if (cell?.rowData?.source === GuestTypeSource.User && !!cell?.rowData?.sourceUser) {
        return `${guestSourceTypeMap[cell?.rowData?.source]} (${cell.rowData.sourceUser.accountName})`;
      }

      return guestSourceTypeMap[cell?.rowData?.source];
    },
  },
  restrictions: {
    key: 'restrictions',
    title: 'Restrictions',
    dataKey: 'restrictions',
    width: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
  visitDate: {
    key: 'visitDate',
    title: 'Visit date',
    dataKey: 'visitDate',
    width: 210,
    sortable: true,
    resizable: true,
    fixed: 1,
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      const local = cell.cellData ? dayjs(cell.cellData, 'YYYY-MM-DD') : null;
      return formatCustomDateTime(local ? local.toDate() : null);
    },
  },
  isShared: {
    key: 'isShared',
    title: 'Is owner?',
    dataKey: 'isShared',
    width: 100,
    sortable: false,
    resizable: true,
    fixed: 1,
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      return (
        <Tag className="eiq-tag" color={cell.cellData ? '#FFB68C' : '#C1FFEA'} style={{ color: '#292B2E' }}>
          {cell.cellData ? 'No' : 'Yes'}
        </Tag>
      );
    },
  },
  carNumber: {
    key: 'carNumber',
    title: 'License plate',
    dataKey: 'carNumber',
    width: 150,
    resizable: true,
    sortable: false,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      const primary = cell?.rowData?.carLicensePlates.find((i) => i.isPrimary);
      return primary?.number;
    },
  },
  carState: {
    key: 'carState',
    title: 'License state',
    dataKey: 'carState',
    width: 150,
    resizable: true,
    sortable: false,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      const primary = cell?.rowData?.carLicensePlates.find((i) => i.isPrimary);
      return primary?.state;
    },
  },
  idDocumentImage: {
    key: 'idDocument',
    title: 'Photo ID',
    dataKey: 'idDocument',
    width: 120,
    resizable: true,
    sortable: false,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IGuestRead>) => {
      const imageUrl = cell?.rowData?.idDocument?.imageUrl;
      if (!imageUrl) {
        return '';
      }

      const isDocument = !!cell?.rowData?.idDocument?.type && cell?.rowData?.idDocument?.type !== 'snapshot';

      return (
        <div style={{ margin: 'auto' }}>
          <EIQPreviewImage
            isDocument={isDocument}
            width={isDocument ? 28 : 20}
            url={imageUrl}
            fileType={UploadFileTypes.IdDocument}
            alt="Id document image"
          />
        </div>
      );
    },
  },
});

const keyPermissionMap: Record<string, string> = {
  true: '#C1FFEA',
  false: '#D1E3FF',
};

export const guestTypeColorMap: Record<GuestType, string> = {
  [GuestType.Undefined]: '#DCDCDC',
  [GuestType.Family]: '#C1FFEA',
  [GuestType.Service]: '#D1E3FF',
  [GuestType.UrgentService]: '#D1E3FF',
  [GuestType.Medical]: '#D1E3FF',
  [GuestType.RideShare]: '#FFD889',
  [GuestType.Delivery]: '#FFD889',
  [GuestType.Guest]: '#EECAFF',
  [GuestType.Employee]: '#ffd1ee',
};

const {
  lastName,
  firstNameWithKey,
  type,
  validity,
  visitDate,
  lastActivityDate,
  phone,
  daysOfWeek,
  keyPermission,
  restrictions,
  companyName,
  isShared,
  source,
  carNumber,
  carState,
  idDocumentImage,
} = guestColumns();

export const eiqSecureGuestInitialColumns = [
  lastName,
  firstNameWithKey,
  companyName,
  type,
  restrictions,
  validity,
  visitDate,
  daysOfWeek,
  lastActivityDate,
  keyPermission,
  isShared,
  phone,
  source,
  carNumber,
  carState,
  idDocumentImage,
];
